import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import type {
  AdminKitchenDetailDto, AdminKitchenDto, AdminOverviewDto, AdminUserDto,
  AuthConfigDto, InviteInfoDto, KitchenDayDto, MeDto, MessageDto, MoveOptionsDto, ProfileDto, RevenueDto, SettingsDto, StaffDto,
  PublicRegistrationDto, RegistrationInboxDto, SubscriberFilter, SubscriberListDto, SubscriptionBriefDto, TodayDto, VersionDto,
} from '@tiffinify/contracts';
import type { Meal } from '@tiffinify/core';
import { get } from './api';

/**
 * After switching kitchens: everything cached belonged to the old kitchen, so it goes, but the signed-in person is
 * replaced in place (not cleared) so what stays on screen, like the visiting bar, sees the change at once.
 */
export function useEnterKitchen() {
  const qc = useQueryClient();
  const nav = useNavigate();
  return (me: MeDto) => {
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
    qc.setQueryData(['me'], me);
    nav('/', { replace: true });
  };
}

export const useMe = () => useQuery({ queryKey: ['me'], queryFn: () => get<MeDto>('/me'), retry: false, staleTime: 5 * 60_000 });
export const useToday = () => useQuery({ queryKey: ['today'], queryFn: () => get<TodayDto>('/today') });

export const useSubscribers = (q: string, filter: SubscriberFilter) =>
  useQuery({
    queryKey: ['subscribers', q, filter],
    queryFn: () => get<SubscriberListDto>(`/subscribers?filter=${filter}&q=${encodeURIComponent(q)}`),
    placeholderData: keepPreviousData,
  });

export const useProfile = (id: string, month?: string) =>
  useQuery({
    queryKey: ['profile', id, month ?? 'now'],
    queryFn: () => get<ProfileDto>(`/subscribers/${id}${month ? `?month=${month}` : ''}`),
    placeholderData: keepPreviousData,
    enabled: !!id,
  });

export const useSettings = () => useQuery({ queryKey: ['settings'], queryFn: () => get<SettingsDto>('/settings') });

export const useKitchenDay = (date: string | undefined, meal: Meal | undefined) =>
  useQuery({
    queryKey: ['kitchen', date, meal],
    queryFn: () => get<KitchenDayDto>(`/kitchen/day?date=${date}&meal=${meal}`),
    enabled: !!date && !!meal,
    placeholderData: keepPreviousData,
  });

export const useRevenue = (month?: string, enabled = true) =>
  useQuery({ queryKey: ['revenue', month ?? 'now'], queryFn: () => get<RevenueDto>(`/revenue${month ? `?month=${month}` : ''}`), placeholderData: keepPreviousData, enabled });

export const useMessage = (subscriptionId: string, type: string) =>
  useQuery({
    queryKey: ['message', subscriptionId, type],
    queryFn: () => get<MessageDto>(`/subscriptions/${subscriptionId}/message?type=${type}`),
    // Switching message type keeps the old text on screen while the new one loads (never another customer's).
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[1] === subscriptionId ? prev : undefined),
  });

export const useBrief = (subscriptionId: string) =>
  useQuery({ queryKey: ['brief', subscriptionId], queryFn: () => get<SubscriptionBriefDto>(`/subscriptions/${subscriptionId}`) });

export const useMoveOptions = (subscriptionId: string) =>
  useQuery({ queryKey: ['move-options', subscriptionId], queryFn: () => get<MoveOptionsDto>(`/subscriptions/${subscriptionId}/move-options`) });

export const useStaff = () => useQuery({ queryKey: ['staff'], queryFn: () => get<StaffDto>('/staff') });

/** What the server is running; compared with this bundle's build stamp in Settings → About. */
export const useVersion = () => useQuery({ queryKey: ['version'], queryFn: () => get<VersionDto>('/version'), staleTime: 0 });

export const useAuthConfig = () => useQuery({ queryKey: ['auth-config'], queryFn: () => get<AuthConfigDto>('/auth/config'), staleTime: Infinity });

export const useInvite = (token: string | undefined) =>
  useQuery({ queryKey: ['invite', token], queryFn: () => get<InviteInfoDto>(`/auth/invite/${token}`), enabled: !!token, retry: false });

export const useRegistrationInbox = () =>
  useQuery({ queryKey: ['registration-inbox'], queryFn: () => get<RegistrationInboxDto>('/registrations') });

export const usePublicRegistration = (token: string | undefined) =>
  useQuery({ queryKey: ['public-registration', token], queryFn: () => get<PublicRegistrationDto>(`/registration/${token}`), enabled: !!token, retry: false });

/* Admin Queries ----------------------------------------------------------- */
export const useAdminOverview = () =>
  useQuery({ queryKey: ['admin-overview'], queryFn: () => get<AdminOverviewDto>('/admin/overview') });

export const useAdminKitchens = () =>
  useQuery({ queryKey: ['admin-kitchens'], queryFn: () => get<AdminKitchenDto[]>('/admin/kitchens') });

export const useAdminKitchenDetail = (id: string | null) =>
  useQuery({
    queryKey: ['admin-kitchen-detail', id],
    queryFn: () => get<AdminKitchenDetailDto>(`/admin/kitchens/${id}`),
    enabled: !!id,
  });

export const useAdminUsers = () =>
  useQuery({ queryKey: ['admin-users'], queryFn: () => get<AdminUserDto[]>('/admin/users') });

